import { ArrowRightIcon, CalendarIcon, CheckIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Button from "components/buttons/Button";
import { format } from "date-fns";
import { SupplierBusyPeriod } from "pages/out-of-office/out-of-office.typing";
import { useBusyPeriodListItem } from "pages/out-of-office/useBusyPeriodListItem";
import { useTranslation } from "react-i18next";
import { useCurrentLanguage } from "utils/language";

interface Props {
  period: SupplierBusyPeriod;
  onEdit: (period: SupplierBusyPeriod) => void;
  onDelete: (id: string) => void;
}

const BusyPeriodListItem = ({ period, onEdit, onDelete }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "outOfOffice.list" });
  const { locale } = useCurrentLanguage();
  const { confirmingDelete, hasComment, startConfirmDelete, cancelConfirmDelete, confirmDelete } =
    useBusyPeriodListItem(period, onDelete);

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-slate-600 dark:bg-slate-700/40"
      data-test={`busy-period-item-${period.id}`}
    >
      <div className="flex shrink-0 items-center justify-center text-gray-400 dark:text-slate-400">
        <CalendarIcon className="size-6" aria-hidden="true" />
      </div>

      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="text-color-primary flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-medium">
          <span className="whitespace-nowrap">{format(period.startDate, "PP", { locale })}</span>
          <ArrowRightIcon className="size-3.5 shrink-0 text-gray-400 dark:text-slate-400" aria-hidden="true" />
          <span className="whitespace-nowrap">{format(period.endDate, "PP", { locale })}</span>
        </p>
        {hasComment ? (
          <p className="text-color-secondary mt-0.5 truncate text-sm" title={period.comments}>
            {period.comments}
          </p>
        ) : null}
      </div>

      {confirmingDelete ? (
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <span className="text-color-secondary hidden text-sm sm:inline">{t("confirmDelete")}</span>
          <Button
            type="button"
            color="ternary"
            label={t("cancelDelete")}
            className="px-3 py-1.5 text-sm"
            onClick={cancelConfirmDelete}
            dataTestId={`busy-period-cancel-delete-${period.id}`}
          />
          <Button
            type="button"
            color="primary"
            label={t("confirmDeleteAction")}
            className="px-3 py-1.5 text-sm"
            onClick={confirmDelete}
            dataTestId={`busy-period-confirm-delete-${period.id}`}
          />
        </div>
      ) : (
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {period.canAcceptSmallAssignments ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-slate-100 px-2 py-1 text-sm font-medium text-slate-600 dark:bg-slate-600 dark:text-slate-200"
              data-test="small-assignments-badge"
            >
              <CheckIcon className="size-3.5" aria-hidden="true" />
              {t("smallAssignments")}
            </span>
          ) : null}
          <button
            type="button"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-slate-200 dark:hover:bg-slate-600 dark:hover:text-slate-200 dark:focus:ring-slate-600"
            onClick={() => onEdit(period)}
            title={t("edit")}
            data-test={`busy-period-edit-${period.id}`}
          >
            <span className="sr-only">{t("edit")}</span>
            <PencilIcon className="size-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-slate-200 dark:hover:bg-slate-600 dark:hover:text-red-400 dark:focus:ring-slate-600"
            onClick={startConfirmDelete}
            title={t("delete")}
            data-test={`busy-period-delete-${period.id}`}
          >
            <span className="sr-only">{t("delete")}</span>
            <TrashIcon className="size-5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default BusyPeriodListItem;
